import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Users,
  Clock,
  Mail,
  Globe,
  Calendar,
  Activity,
  Pause,
  Play,
  Trash2,
  RotateCcw,
} from 'lucide-react';
import { usePlatformOrganizations } from '../../../hooks/platform-admin/usePlatformOrganizations';
import { useUsageAnalytics } from '../../../hooks/platform-admin/useUsageAnalytics';
import { Organization } from '../../../services/platform-admin/platform-organizations.service';
import { OrganizationUsage } from '../../../services/platform-admin/usage-analytics.service';
import PauseOrganizationModal from './modals/PauseOrganizationModal';
import DeleteOrganizationModal from './modals/DeleteOrganizationModal';

export default function OrganizationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    getOrganizationById,
    resumeOrganization,
    restoreOrganization,
    isLoading,
    error,
  } = usePlatformOrganizations();
  const { getOrganizationUsage } = useUsageAnalytics();

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [usage, setUsage] = useState<OrganizationUsage | null>(null);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const loadOrganization = async () => {
    if (!id) return;
    const result = await getOrganizationById(id);
    if (result) {
      setOrganization(result);
    }

    // Load usage data for last 30 days
    const endDate = new Date().toISOString().split('T')[0];
    const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const usageResult = await getOrganizationUsage(id, startDate, endDate);
    if (usageResult) {
      setUsage(usageResult);
    }
  };

  useEffect(() => {
    loadOrganization();
  }, [id]);

  const handleResume = async () => {
    if (!organization) return;
    if (window.confirm(`Resume organization "${organization.name}"?`)) {
      const result = await resumeOrganization(organization.id);
      if (result) {
        await loadOrganization();
      }
    }
  };

  const handleRestore = async () => {
    if (!organization) return;
    if (window.confirm(`Restore deleted organization "${organization.name}"?`)) {
      const result = await restoreOrganization(organization.id);
      if (result) {
        await loadOrganization();
      }
    }
  };

  const getStatusBadge = (status: Organization['status']) => {
    const colors = {
      ACTIVE: 'bg-green-100 text-green-800',
      PAUSED: 'bg-yellow-100 text-yellow-800',
      TRIAL: 'bg-blue-100 text-blue-800',
      DELETED: 'bg-red-100 text-red-800',
      SUSPENDED: 'bg-gray-100 text-gray-800',
    };

    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${colors[status]}`}>
        {status}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toLocaleString();
  };

  const formatBytes = (bytes: number) => {
    const numBytes = typeof bytes === 'string' ? parseInt(bytes) : bytes;
    if (numBytes >= 1073741824) {
      return `${(numBytes / 1073741824).toFixed(2)} GB`;
    }
    if (numBytes >= 1048576) {
      return `${(numBytes / 1048576).toFixed(2)} MB`;
    }
    if (numBytes >= 1024) {
      return `${(numBytes / 1024).toFixed(2)} KB`;
    }
    return `${numBytes} B`;
  };

  if (isLoading && !organization) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading organization...</div>
      </div>
    );
  }

  if (error && !organization) {
    return (
      <div className="space-y-4">
        <Link
          to="/platform-admin/organizations"
          className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Organizations
        </Link>
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="space-y-4">
        <Link
          to="/platform-admin/organizations"
          className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Organizations
        </Link>
        <div className="text-gray-500">Organization not found</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to="/platform-admin/organizations"
          className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Organizations
        </Link>

        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-gradient-to-r from-orange-600 to-red-600 rounded-lg">
              <Building2 className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{organization.name}</h1>
              <div className="flex items-center gap-3 mt-2">
                {getStatusBadge(organization.status)}
                <span className="text-sm text-gray-500">ID: {organization.id}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            {organization.status === 'PAUSED' && (
              <button
                onClick={handleResume}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Play className="h-4 w-4" />
                Resume
              </button>
            )}
            {organization.status === 'ACTIVE' && (
              <button
                onClick={() => setShowPauseModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
              >
                <Pause className="h-4 w-4" />
                Pause
              </button>
            )}
            {organization.status === 'DELETED' && (
              <button
                onClick={handleRestore}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <RotateCcw className="h-4 w-4" />
                Restore
              </button>
            )}
            {organization.status !== 'DELETED' && (
              <button
                onClick={() => setShowDeleteModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600">Total Users</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {organization._count?.users || 0}
              </div>
            </div>
            <Users className="h-8 w-8 text-orange-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600">Total Agents</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {organization._count?.agents || 0}
              </div>
            </div>
            <Users className="h-8 w-8 text-orange-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600">Last Activity</div>
              <div className="text-sm font-semibold text-gray-900 mt-1">
                {formatDate(organization.lastActivityAt)}
              </div>
            </div>
            <Activity className="h-8 w-8 text-orange-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600">Created</div>
              <div className="text-sm font-semibold text-gray-900 mt-1">
                {formatDate(organization.createdAt)}
              </div>
            </div>
            <Calendar className="h-8 w-8 text-orange-600" />
          </div>
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Organization Details */}
        <div className="bg-white rounded-lg shadow border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Organization Details</h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-start gap-3">
              <Building2 className="h-5 w-5 text-gray-400 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm text-gray-600">Organization Name</div>
                <div className="text-base font-medium text-gray-900 mt-1">
                  {organization.name}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Globe className="h-5 w-5 text-gray-400 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm text-gray-600">Timezone</div>
                <div className="text-base font-medium text-gray-900 mt-1">
                  {organization.timezone}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm text-gray-600">Billing Email</div>
                <div className="text-base font-medium text-gray-900 mt-1">
                  {organization.billingEmail || 'Not set'}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm text-gray-600">Status</div>
                <div className="mt-1">{getStatusBadge(organization.status)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Status Information */}
        <div className="bg-white rounded-lg shadow border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Status Information</h2>
          </div>
          <div className="p-6 space-y-4">
            {organization.pausedAt && (
              <>
                <div className="flex items-start gap-3">
                  <Pause className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm text-gray-600">Paused At</div>
                    <div className="text-base font-medium text-gray-900 mt-1">
                      {formatDate(organization.pausedAt)}
                    </div>
                  </div>
                </div>

                {organization.pausedReason && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="text-sm font-medium text-yellow-900 mb-1">Pause Reason</div>
                    <div className="text-sm text-yellow-800">{organization.pausedReason}</div>
                  </div>
                )}
              </>
            )}

            {organization.deletedAt && (
              <div className="flex items-start gap-3">
                <Trash2 className="h-5 w-5 text-red-600 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm text-gray-600">Deleted At</div>
                  <div className="text-base font-medium text-gray-900 mt-1">
                    {formatDate(organization.deletedAt)}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm text-gray-600">Last Updated</div>
                <div className="text-base font-medium text-gray-900 mt-1">
                  {formatDate(organization.updatedAt)}
                </div>
              </div>
            </div>

            {!organization.pausedAt && !organization.deletedAt && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="text-sm text-green-800">
                  Organization is currently active and operational
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Usage & Activity */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Usage & Activity</h2>
            {usage && (
              <div className="text-sm text-gray-600">
                {new Date(usage.dateRange.start).toLocaleDateString()} -{' '}
                {new Date(usage.dateRange.end).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
        <div className="p-6">
          {usage ? (
            <div className="space-y-6">
              {/* Usage Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                  <div className="text-xs text-blue-700 font-medium uppercase">Users</div>
                  <div className="text-2xl font-bold text-blue-900 mt-1">
                    {formatNumber(usage.totals.users)}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
                  <div className="text-xs text-green-700 font-medium uppercase">Agents</div>
                  <div className="text-2xl font-bold text-green-900 mt-1">
                    {formatNumber(usage.totals.agents)}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
                  <div className="text-xs text-purple-700 font-medium uppercase">API Calls</div>
                  <div className="text-2xl font-bold text-purple-900 mt-1">
                    {formatNumber(usage.totals.apiCalls)}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
                  <div className="text-xs text-orange-700 font-medium uppercase">Webhooks</div>
                  <div className="text-2xl font-bold text-orange-900 mt-1">
                    {formatNumber(usage.totals.webhooks)}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-4 rounded-lg border border-pink-200">
                  <div className="text-xs text-pink-700 font-medium uppercase">Storage</div>
                  <div className="text-2xl font-bold text-pink-900 mt-1">
                    {formatBytes(usage.totals.storage)}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-4 rounded-lg border border-indigo-200">
                  <div className="text-xs text-indigo-700 font-medium uppercase">Integrations</div>
                  <div className="text-2xl font-bold text-indigo-900 mt-1">
                    {formatNumber(usage.totals.integrations)}
                  </div>
                </div>
              </div>

              {/* Daily Metrics Table */}
              {usage.dailyMetrics && usage.dailyMetrics.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Daily Breakdown</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Date
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                            Users
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                            Agents
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                            API Calls
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                            Webhooks
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                            Storage
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                            Integrations
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {usage.dailyMetrics.slice(0, 10).map((metric) => (
                          <tr key={metric.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                              {new Date(metric.metricDate).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                              {formatNumber(metric.userCount)}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                              {formatNumber(metric.agentCount)}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                              {formatNumber(metric.apiCallCount)}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                              {formatNumber(metric.webhookCount)}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                              {formatBytes(parseInt(metric.storageBytes))}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                              {formatNumber(metric.integrationCount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {usage.dailyMetrics.length > 10 && (
                    <div className="text-center text-sm text-gray-500 mt-3">
                      Showing 10 of {usage.dailyMetrics.length} days
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              {isLoading ? 'Loading usage data...' : 'No usage data available'}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showPauseModal && (
        <PauseOrganizationModal
          organization={organization}
          onClose={() => setShowPauseModal(false)}
          onSuccess={() => {
            setShowPauseModal(false);
            loadOrganization();
          }}
        />
      )}

      {showDeleteModal && (
        <DeleteOrganizationModal
          organization={organization}
          onClose={() => setShowDeleteModal(false)}
          onSuccess={() => {
            setShowDeleteModal(false);
            navigate('/platform-admin/organizations');
          }}
        />
      )}
    </div>
  );
}
